import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { heroImage, heroCrumbSprites as rawCrumbSprites, heroCrumbFlights, heroVapor as rawVapor } from '@/mocks/home';
import { cdn } from '@/config/cdn';

const heroCrumbSprites = rawCrumbSprites.map(cdn);
const heroVapor = { mask: cdn(rawVapor.mask), noise: rawVapor.noise.map(cdn) };


/** Impact keyframe of `fold-drop` = 0.3s CSS delay + 52% of its 1.6s duration. */
const DROP_DELAY_S = 0.3;
const DROP_IMPACT_S = 1.6 * 0.52;
const CRUMB_SIZE = 2.2;
/** Croissant outline as an ellipse inside the product box (fractions of box width/height). */
const RIM = { cx: 0.5, cy: 0.53, rx: 0.36, ry: 0.27 };
/** Dim bakery scene behind the stage, and the wooden board the croissant lands on. */
const SCENE_BG = cdn('https://public.readdy.ai/ai/img_res/sandbox/3f1aaab66cd17eb8c8e3638ae7fd21c0/dae56e5b-1236-4064-8146-226bddbfd4d4_hero-scene.webp');
const HERO_SRC = cdn(heroImage.src);


/**
 * Black product theatre. The croissant drops in on load, then keeps falling
 * (down + rotating) as the hero scrolls away.
 */
export default function Hero() {
  const stageRef = useRef<HTMLElement>(null);
  const fallRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const crumbLayerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [crumbsLive, setCrumbsLive] = useState(true);
  const [ready, setReady] = useState(false);
  const [linesIn, setLinesIn] = useState(false);
  const [steam, setSteam] = useState<'off' | 'on' | 'paused'>('off');
  const steamStarted = steam !== 'off';
  const [vaporReady, setVaporReady] = useState(false);

  // Preload mask + noise so the fade-in plays on fully loaded images (no pop-in).
  useEffect(() => {
    let alive = true;
    const urls = [heroVapor.mask, ...heroVapor.noise];
    Promise.all(urls.map((u) => new Promise<void>((res) => {
      const im = new Image();
      im.onload = () => res();
      im.onerror = () => res();
      im.src = u;
    }))).then(() => { if (alive) setVaporReady(true); });
    return () => { alive = false; };
  }, []);

  // Pause steam while the hero is offscreen.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !steamStarted || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(([e]) => setSteam(e.isIntersecting ? 'on' : 'paused'));
    io.observe(stage);
    return () => io.disconnect();
  }, [steamStarted]);

  // Sequence gate: nothing plays until the croissant photo is really loaded.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    if (img.complete && img.naturalWidth > 0) { setReady(true); return undefined; }
    const go = () => setReady(true);
    img.addEventListener('load', go);
    img.addEventListener('error', go);
    return () => { img.removeEventListener('load', go); img.removeEventListener('error', go); };
  }, []);


  useEffect(() => {
    const stage = stageRef.current;
    const fall = fallRef.current;
    if (!stage || !fall) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let raf = 0;
    const update = () => {
      raf = 0;
      const h = stage.offsetHeight || window.innerHeight;
      const p = Math.min(Math.max(window.scrollY / h, 0), 1);
      const eased = p * p;
      fall.style.transform = `translate3d(0, ${(eased * 70).toFixed(2)}vh, 0) rotate(${(p * 28).toFixed(2)}deg) scale(${(1 - p * 0.12).toFixed(3)})`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Crumb scatter: synced to the running CSS drop animation's clock, fires at its impact keyframe, plays once.
  useEffect(() => {
    if (!ready) return undefined;
    const drop = dropRef.current;
    const layer = crumbLayerRef.current;
    if (!drop || !layer) return undefined;
    // Crumbs follow the drop: same reduced-motion rule as the CSS drop animation, nothing else.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLinesIn(true);
      setCrumbsLive(false);
      return undefined;
    }
    // Read where the CSS drop animation is right now, so impact lines up even if this effect runs late.
    let elapsed = 0;
    const anim = typeof drop.getAnimations === 'function' ? drop.getAnimations()[0] : undefined;
    if (anim && typeof anim.currentTime === 'number') elapsed = anim.currentTime / 1000;
    const wait = Math.max(DROP_DELAY_S + DROP_IMPACT_S - elapsed, 0);

    const k = Math.min(Math.max(layer.offsetWidth / 1100, 0.45), 1.1);
    const isMobile = window.innerWidth < 768;
    const spread = isMobile ? 0.75 : 1.5;
    const bw = layer.offsetWidth;
    const bh = layer.offsetHeight;
    const nodes = crumbLayerRef.current ? Array.from(layer.querySelectorAll<HTMLElement>('[data-crumb]')) : [];
    const tl = gsap.timeline({ delay: wait, onComplete: () => setCrumbsLive(false) });
    tl.addLabel('impact', 0);
    // Headline: enters after the drop has fully settled (drop ends 1.6s after start, impact is at 0.832s).
    const settled = 1.6 - DROP_IMPACT_S + 0.05;
    tl.addLabel('settled', `impact+=${settled}`);
    tl.call(() => setSteam('on'), undefined, 'settled+=0.4');
    const lines = Array.from(stageRef.current?.querySelectorAll<HTMLElement>('[data-hero-line]') ?? []);
    lines.forEach((line, i) => {
      const fromLeft = line.dataset.heroLine !== '2';
      tl.fromTo(
        line,
        { xPercent: fromLeft ? -120 : 120, opacity: 0 },
        { xPercent: 0, opacity: 1, duration: 1, ease: 'expo.out', immediateRender: false },
        `impact+=${settled + i * 0.14}`,
      );
    });
    nodes.forEach((el) => {
      const f = heroCrumbFlights[Number(el.dataset.crumb)];
      if (isMobile && !f.m) { gsap.set(el, { display: 'none' }); return; }
      const rad = (f.a * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      // Start on the croissant's rim, fly outward, arc up, then gravity pulls down.
      const x0 = (RIM.rx * cos) * bw;
      const y0 = (RIM.cy - 0.5 + RIM.ry * sin) * bh;
      const dist = f.dist * k * spread;
      const peakY = y0 + sin * dist * 0.35 - f.up * k * spread;
      const endY = y0 + f.fall * k * spread;
      const life = f.t * 1.35;
      const tUp = life * 0.34;
      // Pop out at full opacity the instant of impact: a hard burst, not a fade-in.
      gsap.set(el, { x: x0 * 0.85, y: y0 * 0.85, rotate: 0, scale: 0.2, opacity: 0 });
      const at = `impact+=${f.d * 0.5}`;
      tl.set(el, { opacity: 1 }, at)
        .to(el, { scale: 1.15, duration: 0.09, ease: 'power3.out' }, at)
        .to(el, { scale: 1, duration: 0.25, ease: 'power1.out' }, `${at}+=0.09`)
        .to(el, { x: x0 + cos * dist, rotate: f.r * 1.3, duration: life, ease: 'power2.out' }, at)
        .to(el, { y: peakY, duration: tUp, ease: 'power3.out' }, at)
        .to(el, { y: endY, duration: life - tUp, ease: 'power2.in' }, `${at}+=${tUp}`)
        .to(el, { opacity: 0, scale: 0.7, duration: life * 0.14, ease: 'power1.in' }, `${at}+=${life * 0.86}`);
    });
    return () => { tl.kill(); };
  }, [ready]);

  const type = 'fold-serif uppercase leading-[0.86] text-[#F5F1EB] text-[17vw] md:text-[12.5vw] xl:text-[11.5rem]';
  return (
    <section ref={stageRef} className="relative bg-[#0F0E0E] text-[#F5F1EB] min-h-[100svh] overflow-hidden" data-hero-stage data-hero-ready={ready ? 'true' : 'false'} data-hero-lines={linesIn ? 'true' : 'false'}>
      <div className="fold-hero-scene absolute inset-0 z-0" aria-hidden="true">
        <img src={SCENE_BG} alt="" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 fold-hero-scene-shade" />
      </div>
      <h1 className="sr-only">FOLD. — An art in every layer. Artisan bakery, made in layers.</h1>

      <div className="fold-shell relative min-h-[100svh] flex flex-col pt-24 md:pt-28 pb-8 md:pb-10">
        <div className="relative flex-1 flex flex-col justify-center py-6" aria-hidden="true">
          <div className={`${type} relative z-0`} data-hero-line="1">An Art</div>

          <div
            className="pointer-events-none absolute z-10 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vw] h-[112vw] md:w-[80vw] md:h-[60vw] xl:w-[1100px] xl:h-[820px] md:left-[57%]"
            data-hero-product
          >
            <div className="fold-hero-shadow" aria-hidden="true" />
            <div ref={fallRef} className="fold-hero-fall relative w-full h-full">
              <div ref={dropRef} className="fold-hero-drop relative w-full h-full">
                <div className="fold-hero-halo" aria-hidden="true" />
                <img ref={imgRef} src={HERO_SRC} alt={heroImage.alt} title="FOLD. hand-laminated croissant" className="fold-hero-product relative w-full h-full object-contain object-center" />
                <div className="fold-hero-glow" data-glow={steamStarted ? 'on' : 'off'} aria-hidden="true" />
              </div>
              {steamStarted && vaporReady && (
                <div
                  className="fold-vapor"
                  data-steam={steam}
                  aria-hidden="true"
                  style={{ '--vapor-mask': `url(${heroVapor.mask})` } as CSSProperties}
                >
                  <div className="fold-vapor-sway fold-vapor-sway-a">
                    <div className="fold-vapor-field fold-vapor-a" style={{ backgroundImage: `url(${heroVapor.noise[0]})` }} />
                  </div>
                  <div className="fold-vapor-sway fold-vapor-sway-b">
                    <div className="fold-vapor-field fold-vapor-b" style={{ backgroundImage: `url(${heroVapor.noise[1]})` }} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {crumbsLive && (
            <div
              ref={crumbLayerRef}
              className="pointer-events-none absolute z-[25] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vw] h-[112vw] md:w-[80vw] md:h-[60vw] xl:w-[1100px] xl:h-[820px] md:left-[57%]"
              aria-hidden="true"
            >
              {heroCrumbFlights.map((f, i) => (
                <img
                  key={`crumb-${i}`}
                  data-crumb={i}
                  src={heroCrumbSprites[f.s]}
                  alt=""
                  className="fold-crumb absolute left-1/2 top-1/2 h-auto opacity-0 will-change-transform"
                  style={{ width: `${((f.w * CRUMB_SIZE) / 1100) * 100}%`, marginLeft: `-${((f.w * CRUMB_SIZE) / 2200) * 100}%`, marginTop: `-${((f.w * CRUMB_SIZE) / 2200) * 100}%` }}
                />
              ))}
            </div>
          )}

          <div className={`${type} fold-hero-outline relative z-20 text-right italic`} data-hero-line="2">in Every</div>
          <div className={`${type} relative z-20`} data-hero-line="3">Layer.</div>
        </div>

        <div className="relative z-30 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <p className="fold-label text-[rgba(245,241,235,0.5)]">Hand-laminated · Baked at dawn</p>
          <div className="flex flex-wrap items-center gap-8">
            <a href="#craft" className="fold-label fold-link whitespace-nowrap cursor-pointer">Our Craft</a>
            <Link
              to="/collection"
              className="fold-label whitespace-nowrap bg-[#F5F1EB] text-[#171515] px-6 py-4 hover:bg-white transition-colors cursor-pointer"
            >
              Explore Collection
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}